import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';

// ── Design tokens ────────────────────────────────────────────────────────────
const PRIMARY = '#003479';
const TEAL    = '#20C997';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const pct = (n) => (n === null || n === undefined) ? '—' : `${n}%`;

const attendanceBadge = (status) => {
  if (status === 'present') return 'bg-emerald-100 text-emerald-700';
  if (status === 'late')    return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const attendanceLabel = (status) => {
  if (status === 'present') return 'Present';
  if (status === 'late')    return 'Late';
  return 'Absent';
};

const rateColor = (rate) => {
  if (rate >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (rate >= 60) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

const rateBarColor = (rate) => {
  if (rate >= 80) return '#20C997';
  if (rate >= 60) return '#f59e0b';
  return '#ef4444';
};

const scoreColor = (score) => {
  if (score >= 8) return '#20C997';
  if (score >= 5) return '#f59e0b';
  return '#ef4444';
};

const subtopicChip = (status) => {
  if (status === 'good')       return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'needs_work') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-500 border-gray-200';
};

const subtopicLabel = (status) => {
  if (status === 'good')       return 'Good';
  if (status === 'needs_work') return 'Needs Work';
  return 'Not Started';
};

const submissionBadge = (status) => {
  if (status === 'graded')    return 'bg-emerald-100 text-emerald-700';
  if (status === 'submitted') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-500';
};

const submissionLabel = (status) => {
  if (status === 'graded')    return 'Graded';
  if (status === 'submitted') return 'Submitted';
  return 'Pending';
};

// ── SVG Readiness Ring ────────────────────────────────────────────────────────

const ReadinessRing = ({ label, value }) => {
  const r = 36;
  const circumference = 2 * Math.PI * r;
  const pctVal = value ?? 0;
  const dash = (pctVal / 100) * circumference;
  const ringColor = pctVal >= 70 ? '#20C997' : pctVal >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
          {value !== null && value !== undefined && (
            <circle
              cx="44" cy="44" r={r} fill="none"
              stroke={ringColor} strokeWidth="8"
              strokeDasharray={`${dash} ${circumference}`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-gray-800">
            {value !== null && value !== undefined ? `${value}%` : '—'}
          </span>
        </div>
      </div>
      <p className="text-xs font-medium text-gray-500 text-center leading-tight">{label}</p>
    </div>
  );
};

// ── Batch Report Table Row ────────────────────────────────────────────────────

const BatchStudentRow = ({ student, onViewCard }) => {
  const rd = student.readiness;

  return (
    <tr className="hover:bg-gray-50/60 transition-colors">
      {/* Student */}
      <td className="px-5 py-4">
        <p className="font-semibold text-gray-900 text-sm">{student.student_name}</p>
        <p className="text-xs text-gray-400 mt-0.5">{student.student_email}</p>
      </td>

      {/* Attendance */}
      <td className="px-4 py-4">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${rateColor(student.attendance_rate)}`}>
          {pct(student.attendance_rate)}
        </span>
        <p className="text-xs text-gray-400 mt-1">{student.present_count}/{student.total_classes} classes</p>
      </td>

      {/* Skill Score */}
      <td className="px-4 py-4">
        {student.avg_topic_score !== null && student.avg_topic_score !== undefined ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-100 rounded-full h-1.5" style={{ minWidth: 60 }}>
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${(student.avg_topic_score / 10) * 100}%`, background: scoreColor(student.avg_topic_score) }}
                />
              </div>
              <span className="text-xs font-bold text-gray-700">{student.avg_topic_score}/10</span>
            </div>
            <p className="text-xs text-gray-400">{student.rated_topics}/{student.total_topics} topics rated</p>
          </>
        ) : (
          <span className="text-xs text-gray-400">Not rated</span>
        )}
      </td>

      {/* Readiness */}
      <td className="px-4 py-4">
        {rd ? (
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'interview_readiness', label: 'INT' },
              { key: 'resume_readiness',    label: 'RES' },
              { key: 'job_readiness',       label: 'JOB' },
              { key: 'communication_skills',label: 'COM' },
            ].map(({ key, label }) => {
              const val = rd[key];
              if (val === null || val === undefined) return null;
              const color = val >= 70 ? 'bg-emerald-100 text-emerald-700' : val >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
              return (
                <span key={key} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${color}`}>
                  {label} {val}%
                </span>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Assignments */}
      <td className="px-4 py-4">
        <p className="text-sm text-gray-700 font-medium">
          {student.submitted_count}/{student.total_assignments}
        </p>
        <p className="text-xs text-gray-400">{student.graded_count} graded</p>
      </td>

      {/* Grades */}
      <td className="px-4 py-4">
        {student.grades && student.grades.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {student.grades.map((g, i) => (
              <span key={i} className="px-1.5 py-0.5 text-xs font-bold rounded-lg"
                style={{ background: 'rgba(0,52,121,0.08)', color: PRIMARY }}>
                {g}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Action */}
      <td className="px-4 py-4">
        <button
          onClick={() => onViewCard(student)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors"
          style={{ background: 'rgba(0,52,121,0.08)', color: PRIMARY }}
          onMouseEnter={e => { e.currentTarget.style.background = PRIMARY; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,52,121,0.08)'; e.currentTarget.style.color = PRIMARY; }}
        >
          Report Card
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </td>
    </tr>
  );
};

// ── Student Report Card View ──────────────────────────────────────────────────

const StudentReportCard = ({ report, onBack }) => {
  const hasReadiness = report.readiness && Object.values(report.readiness).some(v => v !== null && v !== undefined);
  const hasTopics = report.topics && report.topics.length > 0;
  const ratedTopics = hasTopics ? report.topics.filter(t => t.score !== null).length : 0;
  const avgScore = ratedTopics > 0
    ? (report.topics.filter(t => t.score !== null).reduce((s, t) => s + t.score, 0) / ratedTopics).toFixed(1)
    : null;

  const handlePrint = () => {
    const r = report;

    // ── helpers ──────────────────────────────────────────────────────────────
    const attColor = (rate) => rate >= 80 ? '#20C997' : rate >= 60 ? '#f59e0b' : '#ef4444';
    const skillColor = (s) => s >= 8 ? '#20C997' : s >= 5 ? '#f59e0b' : '#ef4444';

    // ── readiness SVG rings ───────────────────────────────────────────────────
    const ringHtml = (label, value) => {
      const pctVal = value ?? 0;
      const ringR = 34;
      const circ = 2 * Math.PI * ringR;
      const dash = (pctVal / 100) * circ;
      const color = pctVal >= 70 ? '#20C997' : pctVal >= 40 ? '#f59e0b' : '#ef4444';
      return `<div style="display:flex;flex-direction:column;align-items:center;gap:6px;min-width:90px;">
        <div style="position:relative;width:80px;height:80px;">
          <svg width="80" height="80" viewBox="0 0 80 80" style="display:block;transform:rotate(-90deg)">
            <circle cx="40" cy="40" r="${ringR}" fill="none" stroke="#e5e7eb" stroke-width="7"/>
            ${value !== null && value !== undefined
              ? `<circle cx="40" cy="40" r="${ringR}" fill="none" stroke="${color}" stroke-width="7" stroke-dasharray="${dash.toFixed(2)} ${circ.toFixed(2)}" stroke-linecap="round"/>`
              : ''}
          </svg>
          <div style="position:absolute;top:0;left:0;width:80px;height:80px;display:flex;align-items:center;justify-content:center;">
            <span style="font-size:13px;font-weight:700;color:#111827;">${value !== null && value !== undefined ? `${value}%` : '—'}</span>
          </div>
        </div>
        <p style="font-size:10px;color:#6b7280;text-align:center;font-weight:500;max-width:80px;">${label}</p>
      </div>`;
    };

    // ── attendance rows ───────────────────────────────────────────────────────
    const attRows = r.attendance_records && r.attendance_records.length > 0
      ? r.attendance_records.map((rec, i) => {
          const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
          const [sBg, sColor, sLabel] =
            rec.status === 'present' ? ['#d1fae5','#065f46','Present']
            : rec.status === 'late'  ? ['#fef3c7','#92400e','Late']
            :                          ['#fee2e2','#991b1b','Absent'];
          return `<tr style="background:${bg}">
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;">${fmt(rec.scheduled_at)}</td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:500;">${rec.class_title}</td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;">
              <span style="background:${sBg};color:${sColor};padding:2px 9px;border-radius:999px;font-size:10px;font-weight:600;">${sLabel}</span>
            </td>
          </tr>`;
        }).join('')
      : `<tr><td colspan="3" style="padding:16px;text-align:center;color:#9ca3af;">No classes completed yet.</td></tr>`;

    // ── assignment rows ───────────────────────────────────────────────────────
    const asnRows = r.assignments && r.assignments.length > 0
      ? r.assignments.map((asn, i) => {
          const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
          const [sBg, sColor, sLabel] =
            asn.status === 'graded'    ? ['#d1fae5','#065f46','Graded']
            : asn.status === 'submitted' ? ['#dbeafe','#1e40af','Submitted']
            :                              ['#f3f4f6','#6b7280','Pending'];
          return `<tr style="background:${bg}">
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:500;">${asn.title}</td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;">${fmt(asn.due_date)}</td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;">
              <span style="background:${sBg};color:${sColor};padding:2px 9px;border-radius:999px;font-size:10px;font-weight:600;">${sLabel}</span>
            </td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:700;color:#003479;">${asn.grade || '—'}</td>
            <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;font-size:11px;">${asn.feedback || '—'}</td>
          </tr>`;
        }).join('')
      : `<tr><td colspan="5" style="padding:16px;text-align:center;color:#9ca3af;">No assignments yet.</td></tr>`;

    // ── skill topics ──────────────────────────────────────────────────────────
    const topicsHtml = hasTopics
      ? r.topics.map(topic => {
          const s = topic.score;
          const barColor = s !== null && s !== undefined ? skillColor(s) : '#e5e7eb';
          const barW = s !== null && s !== undefined ? `${(s / 10) * 100}%` : '0%';
          const chips = topic.subtopics && topic.subtopics.length > 0
            ? topic.subtopics.map(st => {
                const [cBg, cColor, cLabel] =
                  st.status === 'good'       ? ['#d1fae5','#065f46','Good']
                  : st.status === 'needs_work' ? ['#fef3c7','#92400e','Needs Work']
                  :                              ['#f3f4f6','#6b7280','Not Started'];
                return `<span style="background:${cBg};color:${cColor};padding:2px 8px;border-radius:6px;font-size:10px;font-weight:500;display:inline-block;margin:2px 2px 2px 0;">${st.title} · ${cLabel}</span>`;
              }).join('')
            : '';
          return `<div style="margin-bottom:14px;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">
              <span style="font-weight:600;font-size:12px;color:#111827;">${topic.title}</span>
              <span style="font-size:11px;font-weight:700;color:${barColor};">${s !== null && s !== undefined ? `${s}/10` : 'Not rated'}</span>
            </div>
            <div style="background:#e5e7eb;border-radius:999px;height:6px;margin-bottom:6px;">
              <div style="width:${barW};background:${barColor};border-radius:999px;height:6px;"></div>
            </div>
            <div>${chips}</div>
          </div>`;
        }).join('')
      : '<p style="color:#9ca3af;font-size:12px;">No skill topics configured for this course yet.</p>';

    // ── readiness section ─────────────────────────────────────────────────────
    const readinessHtml = hasReadiness
      ? `<div style="display:flex;gap:20px;flex-wrap:wrap;">
          ${ringHtml('Interview Readiness', r.readiness.interview_readiness)}
          ${ringHtml('Resume Readiness',    r.readiness.resume_readiness)}
          ${ringHtml('Job Readiness',       r.readiness.job_readiness)}
          ${ringHtml('Communication Skills', r.readiness.communication_skills)}
        </div>`
      : '<p style="color:#9ca3af;font-size:12px;">No readiness scores assigned yet.</p>';

    // ── stat accent colors ────────────────────────────────────────────────────
    const ac = attColor(r.attendance_rate);
    const sc = avgScore ? skillColor(parseFloat(avgScore)) : '#9ca3af';

    // ── full HTML ─────────────────────────────────────────────────────────────
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Report Card — ${r.student_name}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #111827; background: #fff; padding: 32px; }
    @media print {
      body { padding: 16px; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>

  <!-- ── Header ── -->
  <div style="border-bottom:3px solid #003479;padding-bottom:16px;margin-bottom:22px;display:flex;justify-content:space-between;align-items:flex-start;">
    <div>
      <h1 style="font-size:22px;font-weight:700;color:#003479;margin-bottom:3px;">${r.student_name}</h1>
      <p style="font-size:12px;color:#6b7280;margin-bottom:4px;">${r.student_email}</p>
      <p style="font-size:13px;font-weight:600;color:#374151;">${r.batch_name}&nbsp;&bull;&nbsp;${r.course_title}</p>
    </div>
    <div style="text-align:right;">
      <p style="font-size:10px;color:#9ca3af;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;">Student Report Card</p>
      <p style="font-size:11px;color:#9ca3af;margin-top:3px;">Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
    </div>
  </div>

  <!-- ── Summary Stats ── -->
  <div style="display:flex;gap:14px;margin-bottom:22px;">
    <div style="flex:1;border:1px solid #e5e7eb;border-radius:10px;padding:14px 16px;border-top:3px solid ${ac};">
      <p style="font-size:26px;font-weight:700;color:${ac};">${r.attendance_rate}%</p>
      <p style="font-size:12px;font-weight:600;color:#374151;margin-top:3px;">Attendance Rate</p>
      <p style="font-size:11px;color:#9ca3af;margin-top:2px;">${r.present_count} present / ${r.total_classes} classes</p>
    </div>
    <div style="flex:1;border:1px solid #e5e7eb;border-radius:10px;padding:14px 16px;border-top:3px solid #20C997;">
      <p style="font-size:26px;font-weight:700;color:#20C997;">${r.submitted_count}/${r.total_assignments}</p>
      <p style="font-size:12px;font-weight:600;color:#374151;margin-top:3px;">Assignments Submitted</p>
      <p style="font-size:11px;color:#9ca3af;margin-top:2px;">${r.graded_count} graded</p>
    </div>
    <div style="flex:1;border:1px solid #e5e7eb;border-radius:10px;padding:14px 16px;border-top:3px solid ${sc};">
      <p style="font-size:26px;font-weight:700;color:${sc};">${avgScore ? `${avgScore}/10` : '—'}</p>
      <p style="font-size:12px;font-weight:600;color:#374151;margin-top:3px;">Avg Skill Score</p>
      <p style="font-size:11px;color:#9ca3af;margin-top:2px;">${ratedTopics > 0 ? `${ratedTopics}/${r.topics.length} topics rated` : 'No skill ratings yet'}</p>
    </div>
  </div>

  <!-- ── Career Readiness ── -->
  <div style="border:1px solid #e5e7eb;border-radius:10px;padding:16px;margin-bottom:22px;">
    <h2 style="font-size:13px;font-weight:700;color:#374151;margin-bottom:14px;padding-bottom:8px;border-bottom:1px solid #f3f4f6;">Career Readiness</h2>
    ${readinessHtml}
  </div>

  <!-- ── Skill Topics ── -->
  <div style="border:1px solid #e5e7eb;border-radius:10px;padding:16px;margin-bottom:22px;">
    <h2 style="font-size:13px;font-weight:700;color:#374151;margin-bottom:14px;padding-bottom:8px;border-bottom:1px solid #f3f4f6;">Skill Topics</h2>
    ${topicsHtml}
  </div>

  <!-- ── Attendance Log ── -->
  <div style="border:1px solid #e5e7eb;border-radius:10px;margin-bottom:22px;overflow:hidden;">
    <h2 style="font-size:13px;font-weight:700;color:#374151;padding:12px 16px;border-bottom:1px solid #f3f4f6;">Attendance Log</h2>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr style="background:#003479;">
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Date</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Class</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Status</th>
        </tr>
      </thead>
      <tbody>${attRows}</tbody>
    </table>
  </div>

  <!-- ── Assignments ── -->
  <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;">
    <h2 style="font-size:13px;font-weight:700;color:#374151;padding:12px 16px;border-bottom:1px solid #f3f4f6;">Assignments</h2>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr style="background:#003479;">
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Assignment</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Due Date</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Status</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Grade</th>
          <th style="color:#fff;text-align:left;padding:8px 12px;font-size:11px;font-weight:600;">Feedback</th>
        </tr>
      </thead>
      <tbody>${asnRows}</tbody>
    </table>
  </div>

</body>
</html>`;

    const win = window.open('', '_blank', 'width=960,height=700');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 600);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Batch Report
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{report.student_name}</h1>
            <p className="text-sm text-gray-500">{report.batch_name} &middot; {report.course_title}</p>
          </div>
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl text-white transition-colors self-start sm:self-auto"
          style={{ background: PRIMARY }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print / Save PDF
        </button>
      </div>

      {/* ── Printable Content ── */}
      <div id="student-report-card" className="space-y-6">

        {/* Print-only header */}
        <div style={{ display: 'none' }}>
          <h1>{report.student_name}</h1>
          <p className="meta">{report.batch_name} &middot; {report.course_title} &middot; {report.student_email}</p>
        </div>

        {/* ── Summary Stat Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stat-row">
          {[
            {
              label: 'Attendance Rate',
              value: `${report.attendance_rate}%`,
              sub: `${report.present_count} present / ${report.total_classes} classes`,
              color: rateBarColor(report.attendance_rate),
              icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
            },
            {
              label: 'Assignments',
              value: `${report.submitted_count}/${report.total_assignments}`,
              sub: `${report.graded_count} graded`,
              color: TEAL,
              icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
            },
            {
              label: 'Avg Skill Score',
              value: avgScore ? `${avgScore}/10` : '—',
              sub: ratedTopics > 0 ? `${ratedTopics}/${report.topics.length} topics rated` : 'No skill ratings yet',
              color: avgScore ? scoreColor(parseFloat(avgScore)) : '#9ca3af',
              icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
            },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 stat-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-2xl font-bold text-gray-900 stat-val" style={{ color: card.color }}>{card.value}</p>
                  <p className="text-sm font-medium text-gray-700 mt-0.5 stat-label">{card.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${card.color}18` }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: card.color }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Career Readiness ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-bold text-gray-900 mb-5">Career Readiness</h2>
          {hasReadiness ? (
            <div className="flex flex-wrap gap-8 justify-center sm:justify-start readiness-row">
              <ReadinessRing label="Interview Readiness" value={report.readiness.interview_readiness} />
              <ReadinessRing label="Resume Readiness"    value={report.readiness.resume_readiness} />
              <ReadinessRing label="Job Readiness"       value={report.readiness.job_readiness} />
              <ReadinessRing label="Communication"       value={report.readiness.communication_skills} />
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-4">No readiness scores assigned yet.</p>
          )}
        </div>

        {/* ── Skill Topics ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-bold text-gray-900 mb-4">Skill Topics</h2>
          {hasTopics ? (
            <div className="space-y-4">
              {report.topics.map((topic, ti) => (
                <div key={ti} className="topic-row">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-800 topic-title">{topic.title}</span>
                    {topic.score !== null && topic.score !== undefined ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${scoreColor(topic.score)}20`, color: scoreColor(topic.score) }}>
                        {topic.score}/10
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not rated</span>
                    )}
                  </div>
                  {/* Score bar */}
                  <div className="score-bar-bg mb-2" style={{ background: '#e5e7eb', borderRadius: 999, height: 6 }}>
                    <div className="score-bar"
                      style={{
                        width: topic.score ? `${(topic.score / 10) * 100}%` : '0%',
                        background: topic.score ? scoreColor(topic.score) : '#e5e7eb',
                        height: 6, borderRadius: 999,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </div>
                  {/* Subtopics */}
                  {topic.subtopics && topic.subtopics.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1 subtopic-chips">
                      {topic.subtopics.map((st, si) => (
                        <span key={si}
                          className={`text-xs px-2.5 py-0.5 rounded-full border font-medium chip ${subtopicChip(st.status)}`}>
                          {st.title}
                          <span className="ml-1 opacity-70 text-[10px]">· {subtopicLabel(st.status)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No skill topics configured for this course yet.</p>
          )}
        </div>

        {/* ── Attendance Log ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-900">Attendance Log</h2>
          </div>
          {report.attendance_records && report.attendance_records.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Class</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.attendance_records.map((rec, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 text-gray-600 text-sm">{fmt(rec.scheduled_at)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{rec.class_title}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold badge ${attendanceBadge(rec.status)}`}>
                          {rec.status === 'present' && (
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {attendanceLabel(rec.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-6 py-8 text-sm text-gray-400 text-center">No classes completed yet.</p>
          )}
        </div>

        {/* ── Assignments ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-bold text-gray-900">Assignments</h2>
          </div>
          {report.assignments && report.assignments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignment</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Grade</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Feedback</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.assignments.map((asn, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 font-medium text-gray-900">{asn.title}</td>
                      <td className="px-4 py-3 text-gray-600">{fmt(asn.due_date)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold badge ${submissionBadge(asn.status)}`}>
                          {submissionLabel(asn.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {asn.grade ? (
                          <span className="font-bold text-gray-900 px-2 py-0.5 rounded-lg text-xs"
                            style={{ background: 'rgba(0,52,121,0.08)', color: PRIMARY }}>
                            {asn.grade}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs max-w-xs">
                        {asn.feedback || <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-6 py-8 text-sm text-gray-400 text-center">No assignments for this batch yet.</p>
          )}
        </div>

      </div>{/* end printable area */}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const StudentReport = () => {
  const [searchParams] = useSearchParams();

  // Mode: 'batch' | 'student'
  const [activeMode, setActiveMode] = useState('batch');

  // Batch selector
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batchLoading, setBatchLoading] = useState(true);

  // Batch report data
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  // Student report card data
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentReport, setStudentReport] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState('');

  // Load batches on mount
  useEffect(() => {
    api.get('/api/batches')
      .then((res) => {
        const list = res.data || [];
        setBatches(list);
        const paramBatchId = searchParams.get('batch_id');
        if (paramBatchId && list.find((b) => b.id === paramBatchId)) {
          setSelectedBatch(paramBatchId);
        }
      })
      .catch(() => {})
      .finally(() => setBatchLoading(false));
  }, [searchParams]);

  // Load batch report when batch changes
  useEffect(() => {
    if (!selectedBatch) { setReport(null); return; }
    setReportLoading(true);
    setReportError('');
    setActiveMode('batch');
    setStudentReport(null);
    api.get(`/api/attendance/report/batch/${selectedBatch}`)
      .then((res) => setReport(res.data))
      .catch(() => setReportError('Failed to load batch report. Please try again.'))
      .finally(() => setReportLoading(false));
  }, [selectedBatch]);

  // Load individual student report card
  useEffect(() => {
    if (!selectedStudentId || !selectedBatch) return;
    setStudentLoading(true);
    setStudentError('');
    api.get(`/api/reports/student/${selectedStudentId}/batch/${selectedBatch}`)
      .then((res) => {
        setStudentReport(res.data);
        setActiveMode('student');
      })
      .catch(() => setStudentError('Failed to load student report. Please try again.'))
      .finally(() => setStudentLoading(false));
  }, [selectedStudentId, selectedBatch]);

  const handleViewCard = (student) => {
    setSelectedStudentId(student.student_id);
  };

  const handleBack = () => {
    setActiveMode('batch');
    setStudentReport(null);
    setSelectedStudentId('');
  };

  // ── Batch selector dropdown ──────────────────────────────────────────────

  const batchSelector = (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">Select Batch</label>
      {batchLoading ? (
        <div className="skeleton h-10 w-72 rounded-xl" />
      ) : (
        <select
          value={selectedBatch}
          onChange={(e) => { setSelectedBatch(e.target.value); setStudentReport(null); setSelectedStudentId(''); setActiveMode('batch'); }}
          className="w-full sm:w-80 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
        >
          <option value="">Select a batch...</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>{b.batch_name}{b.course_title ? ` — ${b.course_title}` : ''}</option>
          ))}
        </select>
      )}
    </div>
  );

  // ── Render student report card mode ──────────────────────────────────────

  if (activeMode === 'student') {
    return (
      <Layout>
        <div className="space-y-4 animate-fade-in">
          {batchSelector}
          {studentLoading && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 flex items-center justify-center">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}
          {studentError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{studentError}</div>
          )}
          {studentReport && !studentLoading && (
            <StudentReportCard report={studentReport} onBack={handleBack} />
          )}
        </div>
      </Layout>
    );
  }

  // ── Render batch report mode ──────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Performance Reports</h1>
            <p className="text-sm text-gray-500 mt-1">
              View batch overview or open an individual student's full report card
            </p>
          </div>
        </div>

        {batchSelector}

        {!selectedBatch && !batchLoading && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center py-20 text-gray-400">
            <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm font-medium text-gray-500">Select a batch to view the report</p>
          </div>
        )}

        {reportError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{reportError}</div>
        )}

        {reportLoading && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 flex items-center justify-center">
            <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}

        {report && !reportLoading && (
          <>
            {/* ── Summary cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Students',         value: report.students.length,             gradient: 'from-[#003479] to-[#005792]' },
                { label: 'Completed Classes', value: report.total_classes,              gradient: 'from-[#20C997] to-[#1B9AAA]' },
                { label: 'Assignments',       value: report.total_assignments,           gradient: 'from-[#ffb547] to-[#ffd166]' },
                {
                  label: 'Avg Attendance',
                  value: report.students.length > 0
                    ? `${Math.round(report.students.reduce((s, st) => s + st.attendance_rate, 0) / report.students.length)}%`
                    : '—',
                  gradient: 'from-[#13263c] to-[#003479]',
                },
              ].map((card) => (
                <div key={card.label} className={`bg-gradient-to-br ${card.gradient} rounded-2xl p-5 text-white shadow-sm`}>
                  <p className="text-2xl font-bold">{card.value}</p>
                  <p className="text-xs opacity-90 mt-0.5">{card.label}</p>
                </div>
              ))}
            </div>

            {/* ── Students table ── */}
            {report.students.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center py-16 text-gray-400">
                <p className="text-sm font-medium text-gray-500">No enrolled students found for this batch.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-base font-bold text-gray-900">{report.batch_name} — Student Overview</h2>
                  <span className="text-xs text-gray-400">{report.students.length} students</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Attendance</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Skill Score</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Readiness</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignments</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Grades</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.students.map((student) => (
                        <BatchStudentRow
                          key={student.student_id}
                          student={student}
                          onViewCard={handleViewCard}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Loading overlay for student card fetch */}
            {studentLoading && (
              <div className="fixed inset-0 z-40 bg-black/20 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-8 shadow-xl flex flex-col items-center gap-3">
                  <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700">Loading report card...</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StudentReport;
